import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { Box, Button, Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfirmDelete from "../../shared/components/ConfirmDelete";
import CustomDataGrid from "../../shared/components/CustomDataGrid";
import StatisticCard from "../../shared/components/StatisticCard";
import { getClientStatusIcon } from "../../shared/functions/getClientStatusIcon";
import { handleError } from "../../shared/functions/handleError";
import { usePagination } from "../../shared/hooks/usePagination";
import { IClient } from "../../shared/interfaces/client/IClient";
import { ICountsByStatus } from "../../shared/interfaces/shared/IBase";
import InvoiceStatisticsSkeleton from "../invoice/components/InvoiceStatisticsSkeleton";
import { useClientColumns } from "./hooks/useClientColumns";
import { ClientService } from "./service";

const AdminClient = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IClient[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<undefined | IClient>(
    undefined,
  );

  const [statistics, setStatistics] = useState<ICountsByStatus | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const columns = useClientColumns(setSelectedClient, setShowDeleteModal);
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 30,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedClient(undefined);
  };

  const deleteHandler = (id?: number) => {
    !!id &&
      ClientService.delete(id)
        .then(() => {
          handleUpdateList();
          enqueueSnackbar(
            t("toasts.successfullyDeleted", { title: selectedClient?.name }),
            {
              variant: "success",
            },
          );
          closeDeleteModal();
        })
        .catch(handleError);
  };

  useEffect(() => {
    ClientService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setStatistics(response?.countsByStatus);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch(handleError);
  }, [pagination, setTotalPages, setCount]);
  return (
    <>
      {statistics === undefined ? (
        <InvoiceStatisticsSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: "20px" }}>
          {Object.keys(statistics)?.map((statistic) => {
            return (
              <StatisticCard
                key={statistic}
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
                isSelected={(pagination?.status as string) === statistic}
                onClick={() => {
                  const status =
                    (pagination?.status as string) === statistic
                      ? undefined
                      : statistic;
                  changeFilterHandler("status", status as string);
                }}
                getTitle={(item) => t(`client.status.${item}`)}
                getIcon={(item) => getClientStatusIcon(item as string)}
                title={statistic}
                getValue={(item) =>
                  String(statistics[item as keyof typeof statistics])
                }
              />
            );
          })}
        </Grid>
      )}
      <Card sx={{ marginTop: "30px" }}>
        <Box
          sx={{
            pb: "20px",
          }}
        >
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                onChange={(e) => {
                  changeFilterHandler("name", e.target.value);
                }}
                label={t("general.searchByName")}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                onChange={(e) => {
                  changeFilterHandler("email", e.target.value);
                }}
                label={t("general.searchByEmail")}
                fullWidth
              />
            </Grid>
            <Grid
              size={{ xs: 12, lg: 6 }}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant={"contained"}
                onClick={() => navigate("/client/create")}
                sx={{ width: { xs: "100%", lg: "auto" } }}
              >
                <AddCircleOutlineOutlinedIcon sx={{ marginRight: "5px" }} />
                {t("button.addNewClient")}
              </Button>
            </Grid>
          </Grid>
        </Box>
        <CustomDataGrid
          columns={columns}
          rows={data || []}
          rowCount={count}
          setPagination={setPagination}
          pagination={pagination}
          loading={data === undefined}
          hasNextPage={totalPages < pagination?.page}
        />

        <ConfirmDelete
          open={showDeleteModal}
          title={selectedClient?.name}
          handleClose={closeDeleteModal}
          handleSubmit={() => deleteHandler(selectedClient?.id)}
        />
      </Card>

    </>
  );
};

export default AdminClient;
